import { useEffect, useState, type ReactNode } from 'react';
import BombiixModal from '../../../BombiixModal/BombiixModal';
import './PimConfirmPopup.css';

export interface PimConfirmPopupProps {
  open: boolean;
  title: ReactNode;
  message: ReactNode;
  warning?: ReactNode;
  onConfirm: () => void;
  onClose: () => void;
  confirmLabel?: string;
  /** Color variant for the confirm button. Defaults to `blue`. */
  confirmVariant?: 'blue' | 'dark' | 'red' | 'light';
  loading?: boolean;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  /** `bar` keeps the confirm button in the modal top bar; `body` centres it under the message (Figma node 13564:537243). */
  confirmPlacement?: 'bar' | 'body';
  /** Extra class on the modal — e.g. `bombiix-modal--top` to anchor it under the module header. */
  className?: string;
  /** When set, the confirm button stays disabled until the user types this phrase. */
  typedPhrase?: string;
  helperText?: string;
}

/**
 * Renders the given helper text with each occurrence of the required phrase
 * bolded and upper-cased, so the typed-confirmation hint stands out.
 */
function renderHelperWithBoldPhrase(text: string, phrase: string): ReactNode[] {
  if (!text || !phrase) return [text];
  const escaped = phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const upper = phrase.toUpperCase();
  const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
  return parts.map((part, index) =>
    part.toLowerCase() === phrase.toLowerCase() ? <strong className="pim-text-bold" key={`phrase-${index}`}>{upper}</strong> : part,
  );
}

/**
 * PIM confirmation popup built on the master-design BombiixModal chrome:
 * blue Confirm on the left, centered title, red close on the right. Supports a
 * plain confirm and an optional typed-phrase gate for destructive flows. Width
 * adapts to content — detail-heavy popups (warning or typed-phrase) default to
 * `md`, plain confirms to `sm`; an explicit `size` prop always wins.
 */
function PimConfirmPopup({
  open,
  title,
  message,
  warning,
  onConfirm,
  onClose,
  confirmLabel = 'Confirm',
  confirmVariant = 'blue',
  confirmPlacement = 'bar',
  loading = false,
  disabled = false,
  size,
  typedPhrase,
  helperText,
  className,
}: PimConfirmPopupProps) {
  const [input, setInput] = useState('');
  const resolvedSize = size ?? (typedPhrase || warning ? 'md' : 'sm');
  const expectedPhrase = (typedPhrase ?? '').toUpperCase();
  const phraseMatched = !typedPhrase || input.trim() === expectedPhrase;
  const resolvedHelperText = helperText ?? (typedPhrase ? `Type ${expectedPhrase} to continue` : '');

  useEffect(() => {
    if (!open) setInput('');
  }, [open]);

  const handleClose = () => {
    setInput('');
    onClose();
  };

  const handleConfirm = () => {
    if (!phraseMatched || loading || disabled) return;
    setInput('');
    onConfirm();
  };

  return (
    <BombiixModal
      show={open}
      title={title}
      size={resolvedSize}
      onClose={handleClose}
      onSave={handleConfirm}
      saveLabel={confirmLabel}
      saveVariant={confirmVariant}
      saveDisabled={!phraseMatched || loading || disabled}
      hideSave={confirmPlacement === 'body'}
      className={className ? `pim-confirm ${className}` : 'pim-confirm'}
    >
      <p className="pim-confirm__message">{message}</p>
      {warning && <p className="pim-confirm__warning">{warning}</p>}
      {typedPhrase && (
        <div className="pim-confirm__typed">
          <label htmlFor="pim-confirm-input">{renderHelperWithBoldPhrase(resolvedHelperText, expectedPhrase)}</label>
          <input
            id="pim-confirm-input"
            type="text"
            value={input}
            onChange={(event) => setInput(event.target.value)}
            placeholder={expectedPhrase}
            autoFocus
            aria-label={resolvedHelperText}
          />
        </div>
      )}
      {confirmPlacement === 'body' && (
        <div className="pim-confirm__actions">
          <button
            type="button"
            className={`bombiix-btn btn-${confirmVariant}`}
            onClick={handleConfirm}
            disabled={!phraseMatched || loading || disabled}
          >
            {confirmLabel}
          </button>
        </div>
      )}
    </BombiixModal>
  );
}

export default PimConfirmPopup;
